import React, { useState, useRef, useMemo, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { StyledWrapper } from './index.styled';

interface DonateFormProps {
  title?: string;
  paypalEmail: string;
  stripePublishableKey?: string;
  onFormSubmitted?: () => void;
  onApplePaySuccess?: (paymentMethodId: string) => void;
}

const transactionFee = 3.5;

const subscriptionTypeTag = {
  monthly: { p3: '1', t3: 'M' },
  quarterly: { p3: '3', t3: 'M' },
  annually: { p3: '1', t3: 'Y' },
  once: null as any,
} as const;

interface ApplePayButtonProps {
  amount: number;
  subscriptionType: string;
  onSuccess?: (paymentMethodId: string) => void;
}

const ApplePayButton = ({ amount, subscriptionType, onSuccess }: ApplePayButtonProps) => {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!stripe || amount <= 0) {
      return undefined;
    }
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label:
          subscriptionType === 'once'
            ? 'One-time Donation'
            : `${subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1)} Donation`,
        amount: Math.round(amount * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then((result) => {
      if (result?.applePay || result?.googlePay || result?.link) {
        setPaymentRequest(pr);
      } else {
        setUnavailable(true);
      }
    });

    pr.on('paymentmethod', async (e: any) => {
      if (onSuccess) {
        onSuccess(e.paymentMethod.id);
      }
      e.complete('success');
    });

    return () => {
      pr.off('paymentmethod');
    };
  }, [stripe, amount, subscriptionType, onSuccess]);

  if (unavailable) {
    return (
      <div className="apple-pay-unavailable">
        <p>Apple Pay is not available on this device or browser.</p>
        <p className="apple-pay-hint">Apple Pay requires Safari on macOS or iOS with a saved card.</p>
      </div>
    );
  }

  if (!paymentRequest) {
    return <div className="apple-pay-loading">Checking Apple Pay availability...</div>;
  }

  return (
    <div className="apple-pay-button-wrapper">
      <button type="button" className="apple-pay-donate-btn" onClick={() => paymentRequest.show()}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
        Donate {subscriptionType} with Apple Pay
      </button>
    </div>
  );
};

const DonateForm = ({
  title,
  paypalEmail,
  stripePublishableKey,
  onFormSubmitted,
  onApplePaySuccess,
}: DonateFormProps) => {
  const [price, setPrice] = useState('50');
  const [coverFeeChecked, setCoverFeeChecked] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<'once' | 'monthly' | 'annually' | 'quarterly'>('once');
  const [paymentMethod, setPaymentMethod] = useState<'paypal' | 'applepay'>('paypal');
  const inputRef = useRef(null);
  const formRef = useRef(null);

  const stripePromise = useMemo(
    () => (stripePublishableKey ? loadStripe(stripePublishableKey) : null),
    [stripePublishableKey],
  );

  const coverFee = useMemo(() => {
    return (parseInt(price, 10) * transactionFee) / 100;
  }, [price]);

  const totalAmount = useMemo(() => {
    const base = parseFloat(price) || 0;
    return coverFeeChecked ? base + coverFee : base;
  }, [price, coverFeeChecked, coverFee]);

  const setPriceAmount = (e: any) => {
    setPrice(e.target.value);
  };

  return (
    <StyledWrapper>
      <div className="donate-form">
        <div className="donateFormHeader">
          <h2 className="donateheadline">
            {title ||
              'How much would you like to donate? As a contributor we make sure your donation goes directly to supporting our cause.'}
          </h2>
        </div>

        <section className="amount-section">
          <p className="title">Choose Amount:</p>
          <div className="input-amount">
            <span className="dollarSign">$</span>
            <input
              type="number"
              id="inputAmount"
              name={subscriptionType === 'once' ? 'amount' : 'a3'}
              value={price}
              onChange={setPriceAmount}
              ref={inputRef}
            />
          </div>

          <div className="amount-buttons">
            <button
              className={`amountPrice${price === '10' ? ' selected' : ''}`}
              type="button"
              onClick={() => setPrice('10')}
            >
              <span className="currency-before">$</span>
              <span className="priceAmount">10</span>
            </button>
            <button
              className={`amountPrice${price === '30' ? ' selected' : ''}`}
              type="button"
              onClick={() => setPrice('30')}
            >
              <span className="currency-before">$</span>
              <span className="priceAmount">30</span>
            </button>
            <button
              className={`amountPrice${price === '50' ? ' selected' : ''}`}
              type="button"
              onClick={() => setPrice('50')}
            >
              <span className="currency-before">$</span>
              <span className="priceAmount">50</span>
            </button>
            <button
              className={`amountPrice${price === '100' ? ' selected' : ''}`}
              type="button"
              onClick={() => setPrice('100')}
            >
              <span className="currency-before">$</span>
              <span className="priceAmount">100</span>
            </button>
            <button
              className={`amountPrice${price === '250' ? ' selected' : ''}`}
              type="button"
              onClick={() => setPrice('250')}
            >
              <span className="currency-before">$</span>
              <span className="priceAmount">250</span>
            </button>
            <button
              className={`amountPrice${!['10', '50', '30', '250', '100'].includes(price) ? ' selected' : ''}`}
              onClick={() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                  setPrice('');
                }
              }}
              id="customAmount"
              type="button"
            >
              <span className="priceAmount">
                Custom
                <br />
                Amount
              </span>
            </button>
          </div>
        </section>

        <section className="subscription-section">
          <p className="title">Choose Period:</p>
          <div className="subscription-buttons">
            <button
              className={`subscriptionBtn${subscriptionType === 'once' ? ' selected' : ''}`}
              onClick={() => setSubscriptionType('once')}
              id="onceSub"
              type="button"
            >
              Once
            </button>
            <button
              className={`subscriptionBtn${subscriptionType === 'monthly' ? ' selected' : ''}`}
              type="button"
              onClick={() => setSubscriptionType('monthly')}
            >
              Monthly
            </button>
            <button
              className={`subscriptionBtn${subscriptionType === 'quarterly' ? ' selected' : ''}`}
              type="button"
              onClick={() => setSubscriptionType('quarterly')}
            >
              Quarterly
            </button>
            <button
              className={`subscriptionBtn${subscriptionType === 'annually' ? ' selected' : ''}`}
              type="button"
              onClick={() => setSubscriptionType('annually')}
            >
              Annually
            </button>
          </div>
        </section>

        <section className="transaction-fee-section">
          <input
            type="checkbox"
            id="transactionFee"
            name="transactionFee"
            value="transactionFee"
            checked={coverFeeChecked}
            onChange={() => setCoverFeeChecked(!coverFeeChecked)}
          />
          <label htmlFor="transactionFee">
            I Like to help to cover the transaction fee of
            <span id="transactionFeeAmount"> ${coverFee.toFixed(2)}</span>
          </label>
        </section>

        <section className="payment-method-section">
          <p className="title">Pay With:</p>
          <div className="payment-method-buttons">
            <button
              type="button"
              className={`paymentMethodBtn${paymentMethod === 'paypal' ? ' selected' : ''}`}
              onClick={() => setPaymentMethod('paypal')}
            >
              <span className="payment-icon paypal-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082H9.824L8.435 21H13.5c.524 0 .968-.382 1.05-.9l.765-4.864h1.84c3.834 0 5.856-1.831 6.567-5.432.29-1.473.05-2.522-.5-3.887z" />
                </svg>
              </span>
              PayPal
            </button>
            <button
              type="button"
              className={`paymentMethodBtn${paymentMethod === 'applepay' ? ' selected' : ''}`}
              onClick={() => setPaymentMethod('applepay')}
            >
              <span className="payment-icon apple-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
              </span>
              Apple Pay
            </button>
          </div>
        </section>

        {paymentMethod === 'paypal' && (
          <form
            target="_blank"
            action="https://www.paypal.com/cgi-bin/webscr"
            method="post"
            id="donationForm"
            ref={formRef}
          >
            <input
              type="hidden"
              name="cmd"
              value={subscriptionType === 'once' ? '_donations' : '_xclick-subscriptions'}
            />
            <input type="hidden" name="currency_code" value="USD" />
            <input type="hidden" name="business" value={paypalEmail} />
            <input type="hidden" name="charset" value="utf-8" />
            <input type="hidden" name="lc" value="US" />
            <input type="hidden" name="no_note" value="1" />
            <input type="hidden" name={subscriptionType === 'once' ? 'amount' : 'a3'} value={price} />

            <div className="donate_buttons">
              <input
                type="submit"
                value={`Donate ${subscriptionType} via PayPal`}
                alt="Donate via PayPal"
                className="btn_styles"
                id="donateBtn"
                onClick={(e) => {
                  e.preventDefault();
                  if (coverFeeChecked) {
                    inputRef.current.value = String(parseInt(price, 10) + coverFee);
                  }
                  if (formRef.current) {
                    formRef.current.submit();
                    if (onFormSubmitted) {
                      onFormSubmitted();
                    }
                  }
                }}
              />
              <img alt="" width="1" height="1" src="https://www.paypalobjects.com/en_US/i/scr/pixel.gif" />
            </div>

            {!!subscriptionTypeTag[subscriptionType] && (
              <>
                <input type="hidden" className="subsciptionTag" name="src" value="1" />
                <input type="hidden" name="no_shipping" value="1" />
                <input
                  type="hidden"
                  className="subsciptionTag"
                  name="p3"
                  value={subscriptionTypeTag[subscriptionType].p3}
                />
                <input
                  type="hidden"
                  className="subsciptionTag"
                  name="t3"
                  value={subscriptionTypeTag[subscriptionType].t3}
                />
              </>
            )}
          </form>
        )}

        {paymentMethod === 'applepay' && (
          <div className="apple-pay-section">
            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <ApplePayButton
                  amount={totalAmount}
                  subscriptionType={subscriptionType}
                  onSuccess={onApplePaySuccess}
                />
              </Elements>
            ) : (
              <div className="apple-pay-unavailable">
                <p>Apple Pay is not configured.</p>
                <p className="apple-pay-hint">Please provide a Stripe publishable key to enable Apple Pay.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </StyledWrapper>
  );
};

export default DonateForm;
